import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DollarSign,
  TrendingUp,
  Calendar,
  Users,
  ChevronLeft,
  ChevronRight,
  Download,
  Percent,
  Banknote,
  CreditCard,
  Package,
  CheckCircle,
  Clock,
  Loader2,
} from "lucide-react";
import { API } from "@/App";
import { format, subMonths, addMonths, startOfMonth, endOfMonth } from "date-fns";
import { tr } from "date-fns/locale";
import { getDateLocale } from "@/utils/dateUtils";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import { useLocalizedData } from "@/contexts/LocalizedDataContext";
import { useCurrency } from "@/contexts/CurrencyContext";
import { useTranslation } from "react-i18next";

const COLORS = ["#1B365D", "#DA291C", "#10B981", "#F59E0B"];

export default function MyCommissionsModule({ user }) {
  const { t } = useTranslation(["common", "dashboard", "members", "trainers", "reservations", "settings", "payments", "rooms", "messaging", "business", "memberDashboard", "reports"]);
  const { formatPrice, currencySymbol } = useCurrency();
  const { getLocalizedBranch, getLocalizedTrainingType } = useLocalizedData();

  const [commissionData, setCommissionData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(new Date());
  const [trainerId, setTrainerId] = useState(null);
  const [trainer, setTrainer] = useState(null);
  const [monthlyHistory, setMonthlyHistory] = useState([]);
  const [commissionDebts, setCommissionDebts] = useState([]); // Verilen prim borçları ve ödeme durumları

  useEffect(() => {
    if (user?.user_id) {
      fetchTrainerAndCommissions();
    }
  }, [user, selectedMonth]);

  // Format date to Turkish format (DD.MM.YYYY)
  const formatDateTR = (dateStr) => {
    if (!dateStr) return "-";
    // Handle different date formats
    let date;
    if (dateStr.includes('T')) {
      date = new Date(dateStr);
    } else if (dateStr.includes('-')) {
      // Format: YYYY-MM-DD
      const parts = dateStr.split('-');
      if (parts.length === 3) {
        return `${parts[2]}.${parts[1]}.${parts[0]}`;
      }
    }
    if (date && !isNaN(date)) {
      return format(date, "dd.MM.yyyy", { locale: getDateLocale() });
    }
    return dateStr;
  };

  const fetchTrainerAndCommissions = async () => {
    try {
      // Get trainer info
      const trainerRes = await fetch(`${API}/trainers/by-user/${user.user_id}`, {
        credentials: "include",
      });
      
      if (trainerRes.ok) {
        const trainers = await trainerRes.json();
        if (trainers.length > 0) {
          const trainerData = trainers[0];
          setTrainerId(trainerData.trainer_id);
          setTrainer(trainerData);
          
          // Get commission data for selected month using new API
          const month = selectedMonth.getMonth() + 1;
          const year = selectedMonth.getFullYear();
          
          const commRes = await fetch(
            `${API}/trainers/${trainerData.trainer_id}/my-commissions?month=${month}&year=${year}`,
            { credentials: "include" }
          );
          
          if (commRes.ok) {
            const data = await commRes.json();
            setCommissionData(data);
          }
          
          // Fetch trainer debts (commission debts - verilen primler)
          // Use my-debts endpoint which is used by MyFinancesModule
          try {
            const debtsRes = await fetch(
              `${API}/trainers/${trainerData.trainer_id}/my-debts`,
              { credentials: "include" }
            );
            if (debtsRes.ok) {
              const debtsData = await debtsRes.json();
              // Get commission debts from commission_debt (not commission!)
              const allCommissionDebts = debtsData?.commission_debt?.debts || [];
              
              console.log("All commission debts from my-debts API (commission_debt.debts):", allCommissionDebts);
              console.log("Full debtsData:", debtsData);
              
              // Keep all debts - we'll match them in the table
              setCommissionDebts(allCommissionDebts);
            }
          } catch (e) {
            console.error("Error fetching trainer debts:", e);
          }
          
          // Fetch last 6 months for history IN PARALLEL (was sequential → ~10s; now ~2s)
          const monthsToFetch = [];
          for (let i = 5; i >= 0; i--) {
            const date = subMonths(new Date(), i);
            monthsToFetch.push({
              date,
              m: date.getMonth() + 1,
              y: date.getFullYear(),
            });
          }
          const historyResults = await Promise.all(
            monthsToFetch.map((mt) =>
              fetch(
                `${API}/trainers/${trainerData.trainer_id}/my-commissions?month=${mt.m}&year=${mt.y}`,
                { credentials: "include" }
              )
                .then((r) => (r.ok ? r.json() : null))
                .then((d) => ({
                  month: format(mt.date, "MMM", { locale: getDateLocale() }),
                  commission: d?.total_commission || 0,
                  classes: d?.total_sessions || 0,
                }))
                .catch(() => ({
                  month: format(mt.date, "MMM", { locale: getDateLocale() }),
                  commission: 0,
                  classes: 0,
                }))
            )
          );
          setMonthlyHistory(historyResults);
        }
      }
    } catch (error) {
      console.error("Error fetching commissions:", error);
    } finally {
      setLoading(false);
    }
  };

  // Group details by type for pie chart
  const getPieData = () => {
    if (!commissionData?.details) return [];
    const byType = {};
    commissionData.details.forEach(d => {
      const type = d.training_type || t("common:other");
      byType[type] = (byType[type] || 0) + 1;
    });
    return Object.entries(byType).map(([name, value]) => ({ name, value }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-navy" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Title */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="font-heading font-bold text-xl text-navy">{t("trainers:myCommissionsTitle")}</h2>
          <p className="text-grey text-sm">{t("reports:aylikPrimVePerformansTakibi")}</p>
        </div>

        {/* Month Selector */}
        <div className="flex items-center gap-2 bg-white rounded-xl border p-1">
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={() => setSelectedMonth(subMonths(selectedMonth, 1))}
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <span className="px-3 font-medium text-navy min-w-[120px] text-center">
            {format(selectedMonth, "MMMM yyyy", { locale: getDateLocale() })}
          </span>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={() => setSelectedMonth(addMonths(selectedMonth, 1))}
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Summary Cards - Tüm antrenörler için hem alınan hem verilen prim göster */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {/* Alınan Prim */}
        <Card className="border-0 shadow-sm bg-purple-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-purple-500" />
              </div>
              <div>
                <p className="text-2xl font-bold text-purple-600">
                  {currencySymbol}{commissionData?.total_commission?.toLocaleString('tr-TR', { minimumFractionDigits: 2 }) || "0.00"}
                </p>
                <p className="text-xs text-grey">{t("reports:receivedCommission")}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        {/* Verilen Prim (Gerçek Ödenen) - sadece değer varsa göster */}
        <Card className={`border-0 shadow-sm ${(commissionData?.external_commission || 0) > 0 ? 'bg-orange-50' : ''}`}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 ${(commissionData?.external_commission || 0) > 0 ? 'bg-orange-100' : 'bg-orange-50'} rounded-xl flex items-center justify-center`}>
                <TrendingUp className={`w-5 h-5 ${(commissionData?.external_commission || 0) > 0 ? 'text-orange-500' : 'text-orange-400'}`} />
              </div>
              <div>
                <p className={`text-2xl font-bold ${(commissionData?.external_commission || 0) > 0 ? 'text-orange-600' : 'text-orange-400'}`}>
                  {currencySymbol}{(commissionData?.external_commission_paid ?? 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                </p>
                <p className="text-xs text-grey">{t("trainers:verilenPrim")}</p>
                {(commissionData?.external_commission_pending || 0) > 0 && (
                  <p className="text-[10px] text-amber-600 mt-0.5">
                    {t("common:pending")}: {currencySymbol}{commissionData.external_commission_pending.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                  </p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
        
        {/* Toplam Ders */}
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center">
                <Calendar className="w-5 h-5 text-blue-500" />
              </div>
              <div>
                <p className="text-2xl font-bold text-navy">
                  {commissionData?.total_sessions || 0}
                </p>
                <p className="text-xs text-grey">{t('common:totalLessons')}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        {/* Net Prim */}
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-50 rounded-xl flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-green-500" />
              </div>
              <div>
                <p className={`text-2xl font-bold ${(commissionData?.net_commission || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {currencySymbol}{commissionData?.net_commission?.toLocaleString('tr-TR', { minimumFractionDigits: 2 }) || "0.00"}
                </p>
                <p className="text-xs text-grey">{t("trainers:netCommission")}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        {/* Bekleyen */}
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-50 rounded-xl flex items-center justify-center">
                <Clock className="w-5 h-5 text-amber-500" />
              </div>
              <div>
                <p className="text-2xl font-bold text-amber-600">
                  {currencySymbol}{commissionData?.pending_amount?.toLocaleString('tr-TR', { minimumFractionDigits: 2 }) || "0.00"}
                </p>
                <p className="text-xs text-grey">{t("trainers:pendingReceivable")}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Toplam Kalan (Kümülatif) */}
        {typeof commissionData?.cumulative_remaining === 'number' && (
          <Card className="border-0 shadow-sm" data-testid="my-cumulative-remaining">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-red-50 rounded-xl flex items-center justify-center">
                  <Clock className="w-5 h-5 text-red-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-red-600">
                    {currencySymbol}{commissionData.cumulative_remaining?.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                  </p>
                  <p className="text-xs text-grey">Toplam Kalan (kümülatif)</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Monthly Trend */}
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-medium">{t("trainers:sixMonthTrend")}</CardTitle>
          </CardHeader>
          <CardContent>
            {monthlyHistory.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={monthlyHistory}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                  <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip
                    formatter={(value, name) => [
                      name === "commission" ? `${currencySymbol}${(value || 0).toLocaleString()}` : value,
                      name === "commission" ? t("common:commissionBadge") : t("common:lessonsSingular"),
                    ]}
                  />
                  <Bar dataKey="commission" fill="#1B365D" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[250px] flex items-center justify-center text-grey">
                {t("trainers:noDataFound")}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Class Type Distribution */}
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-medium">{t("common:lessonTypeDistribution")}</CardTitle>
          </CardHeader>
          <CardContent>
            {getPieData().length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={getPieData()}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    dataKey="value"
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  >
                    {getPieData().map((_, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[250px] flex items-center justify-center text-grey">
                Bu ay ders verisi yok
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Alınan Prim Detayları - işletme üyelerinden kazanılan */}
      {commissionData?.details?.filter(d => !d.is_expense)?.length > 0 && (
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-medium text-purple-700">
              {t("trainers:receivedCommissionDetails")}
            </CardTitle>
            <p className="text-xs text-grey">{t("reports:i̇sletmeUyelerindenKazanilanPrimler")}</p>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="whitespace-nowrap">{t('common:date')}</TableHead>
                    <TableHead className="whitespace-nowrap">{t('common:time')}</TableHead>
                    <TableHead className="whitespace-nowrap">{t("common:member")}</TableHead>
                    <TableHead className="whitespace-nowrap">{t("common:type")}</TableHead>
                    <TableHead className="whitespace-nowrap">{t("common:source")}</TableHead>
                    <TableHead className="text-right whitespace-nowrap">{t('common:amount')}</TableHead>
                    <TableHead className="text-right whitespace-nowrap">{t("common:rate")}</TableHead>
                    <TableHead className="text-right whitespace-nowrap">{t("common:commissionBadge")}</TableHead>
                    <TableHead className="text-center whitespace-nowrap">{t('common:status')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {commissionData.details.filter(d => !d.is_expense).map((d, i, arr) => {
                    // Calculate cumulative commission up to this point
                    const cumulativeCommission = arr.slice(0, i + 1).reduce((sum, item) => sum + (item.commission || 0), 0);
                    const isPaid = cumulativeCommission <= (commissionData?.paid_amount || 0);
                    
                    return (
                      <TableRow key={i}>
                        <TableCell className="whitespace-nowrap">{formatDateTR(d.date)}</TableCell>
                        <TableCell className="whitespace-nowrap">{d.time}</TableCell>
                        <TableCell className="whitespace-nowrap">{d.member_name || "-"}</TableCell>
                        <TableCell className="whitespace-nowrap">{getLocalizedTrainingType(d.training_type)}</TableCell>
                        <TableCell className="whitespace-nowrap">
                          {d.type === "card" ? (
                            <Badge className="bg-amber-100 text-amber-700">
                              <CreditCard className="w-3 h-3 mr-1" />
                              {d.card_name}
                            </Badge>
                          ) : (
                            <Badge className="bg-emerald-100 text-emerald-700">
                              <Package className="w-3 h-3 mr-1" />
                              {d.package_name}
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-right whitespace-nowrap">{currencySymbol}{d.amount?.toLocaleString('tr-TR')}</TableCell>
                        <TableCell className="text-right whitespace-nowrap">
                          {d.rate_type === "percentage" ? `%${d.rate}` : `${currencySymbol}${d.rate}`}
                        </TableCell>
                        <TableCell className="text-right font-medium whitespace-nowrap text-purple-700">
                          +{currencySymbol}{d.commission?.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                        </TableCell>
                        <TableCell className="text-center whitespace-nowrap">
                          {isPaid ? (
                            <Badge className="bg-green-100 text-green-700">
                              <CheckCircle className="w-3 h-3 mr-1" />
                              {t("trainers:receivedStatus")}
                            </Badge>
                          ) : (
                            <Badge className="bg-amber-100 text-amber-700">
                              <Clock className="w-3 h-3 mr-1" />
                              {t("trainers:pendingStatus")}
                            </Badge>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Verilen Prim Detayları - dış üye derslerinden işletmeye ödenen */}
      {commissionData?.details?.filter(d => d.is_expense)?.length > 0 && (
        <Card className="border-0 shadow-sm border-l-4 border-l-orange-500">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-medium text-orange-700">
              {t("trainers:paidCommissionDetails")}
            </CardTitle>
            <p className="text-xs text-grey">{t("reports:disUyeDerslerindenIsletmeyeOdenen")}</p>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="whitespace-nowrap">{t('common:date')}</TableHead>
                    <TableHead className="whitespace-nowrap">{t('common:time')}</TableHead>
                    <TableHead className="whitespace-nowrap">{t("common:member")}</TableHead>
                    <TableHead className="whitespace-nowrap">{t("common:type")}</TableHead>
                    <TableHead className="whitespace-nowrap">{t("common:source")}</TableHead>
                    <TableHead className="text-right whitespace-nowrap">{t('common:amount')}</TableHead>
                    <TableHead className="text-right whitespace-nowrap">{t("common:rate")}</TableHead>
                    <TableHead className="text-right whitespace-nowrap">{t("common:commissionBadge")}</TableHead>
                    <TableHead className="text-center whitespace-nowrap">{t('common:status')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(() => {
                    // FIFO approach: Calculate total paid and distribute across rows
                    const expenseDetails = commissionData.details.filter(d => d.is_expense);
                    
                    // Calculate total paid amount from commissionDebts
                    const totalPaidAmount = commissionDebts.reduce((sum, debt) => sum + (debt.paid_amount || 0), 0);
                    
                    console.log("FIFO calculation:", {
                      totalExpenses: expenseDetails.reduce((sum, d) => sum + (d.commission || 0), 0),
                      totalPaidAmount,
                      debtsCount: commissionDebts.length,
                      commissionDebts
                    });
                    
                    // Sort by date (oldest first) for FIFO
                    const sortedDetails = [...expenseDetails].sort((a, b) => {
                      const dateA = new Date(a.date + 'T' + (a.time || '00:00'));
                      const dateB = new Date(b.date + 'T' + (b.time || '00:00'));
                      return dateA - dateB;
                    });
                    
                    // Track remaining payment to distribute
                    let remainingPayment = totalPaidAmount;
                    
                    return sortedDetails.map((d, i) => {
                      const rowAmount = d.commission || 0;
                      let status = "pending";
                      let paidForThisRow = 0;
                      
                      if (remainingPayment >= rowAmount) {
                        // This row is fully paid
                        status = "paid";
                        paidForThisRow = rowAmount;
                        remainingPayment -= rowAmount;
                      } else if (remainingPayment > 0) {
                        // This row is partially paid
                        status = "partial";
                        paidForThisRow = remainingPayment;
                        remainingPayment = 0;
                      }
                      // else: status stays "pending", paidForThisRow stays 0
                      
                      return (
                        <TableRow key={i} className="bg-orange-50/30">
                          <TableCell className="whitespace-nowrap">{formatDateTR(d.date)}</TableCell>
                          <TableCell className="whitespace-nowrap">{d.time}</TableCell>
                          <TableCell className="whitespace-nowrap">{d.member_name || "-"}</TableCell>
                          <TableCell className="whitespace-nowrap">{getLocalizedTrainingType(d.training_type)}</TableCell>
                          <TableCell className="whitespace-nowrap">
                            <Badge className="bg-orange-100 text-orange-700">
                              {t("trainers:externalMemberLesson")}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right whitespace-nowrap">{currencySymbol}{d.amount?.toLocaleString('tr-TR')}</TableCell>
                          <TableCell className="text-right whitespace-nowrap">
                            {d.rate_type === "percentage" ? `%${d.rate}` : `${currencySymbol}${d.rate}`}
                          </TableCell>
                          <TableCell className="text-right font-medium whitespace-nowrap text-orange-700">
                            -{currencySymbol}{d.commission?.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                          </TableCell>
                          <TableCell className="text-center whitespace-nowrap">
                            {status === "paid" ? (
                              <Badge className="bg-green-100 text-green-700">
                                <CheckCircle className="w-3 h-3 mr-1" />
                                {t("trainers:paidStatus")}
                              </Badge>
                            ) : status === "partial" ? (
                              <Badge className="bg-blue-100 text-blue-700">
                                <Clock className="w-3 h-3 mr-1" />
                                {t("trainers:partialAmount", { amount: `${currencySymbol}${paidForThisRow.toLocaleString('tr-TR')}` })}
                              </Badge>
                            ) : (
                              <Badge className="bg-amber-100 text-amber-700">
                                <Clock className="w-3 h-3 mr-1" />
                                {t("trainers:pendingStatus")}
                              </Badge>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    });
                  })()}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Hesaplama Yöntemi */}
      <Card className="border-0 shadow-sm bg-blue-50">
        <CardContent className="p-4 space-y-2">
          <p className="text-sm text-blue-700">
            <strong>{t("trainers:commissionCalculationMethod")}</strong>{" "}
            {commissionData?.calculation_method === "per_package_sale" 
              ? `📦 ${t("trainers:perPackageSale")}`
              : `📚 ${t("trainers:perLesson")}`
            }
          </p>
          {(commissionData?.external_commission || 0) > 0 && (
            <p className="text-sm text-orange-700">
              <strong>{t("trainers:externalMemberLesson")}:</strong> 🔄 {t("trainers:externalMemberLessonInfo")}
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
